import React, { useState, useEffect, useContext } from 'react'
import {
    Button,
    Dialog,
    DialogContent,
    Divider,
    Typography,
    useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Content, Campo, InfoVenda, InfoVendaLinha, InfoVendaLabel, InfoVendaValor } from './styles'
import axios, { WithAxios } from '../../../services/withAxios';
import { GFLContext } from '../../../infra/GFLContext';
import PopupProps from '../../../interfaces/popupProps';
import NotaFiscal from '../../../interfaces/notafiscal';
import VendaListagem from '../../../interfaces/vendaListagem';
import StatusNotaFiscal from '../../../interfaces/statusNotaFiscal';
import obterDescricaoFormaPagamento from '../../../utils/enum/formaPagamentoDescricao';
import { TypeAlertEnum } from '../../../utils/enum/typeAlertEnum';
import { useConfirm } from 'material-ui-confirm';
import { isStatusCanceladoNotaFiscal, isStatusSucessoNotaFiscal } from '../../../utils/Constants';

const VendaDialog: React.FC<PopupProps<VendaListagem>> = ({ setAberto, entidade }) => {
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
    const contexto = useContext(GFLContext)
    const confirm = useConfirm()

    const [notasFiscais, setNotasFiscais] = useState<NotaFiscal[]>([]);
    const [statusNotaFiscal, setStatusNotaFiscal] = useState<StatusNotaFiscal>(null);

    const possuiNotaFiscal = statusNotaFiscal !== null && statusNotaFiscal.statusRetorno !== 0;

    const handleFechar = () => {
        setAberto(false)
    }

    useEffect(() => {
        if (!entidade)
            return

        const buscarDados = async () => {
            await axios(`/venda/${entidade.id}/getstatusnotafiscal`)
                .then(resp => {
                    setStatusNotaFiscal(resp.data as StatusNotaFiscal)
                })
        }

        buscarDados()
    }, [entidade])

    const cancelarNFCe = () => {
        axios({
            url: `/venda/${entidade.id}/cancelarNotaFiscal`,
            method: 'POST'
        }).then(resp => {
            contexto.setMessageAlert("NFCe cancelada com sucesso!")
            contexto.setTypeAlert(TypeAlertEnum.success)
            contexto.setOpenAlert(true)

            setAberto(false)
        }).catch(error => {
            contexto.setMessageAlert("Erro ao cancelar NFCe! " + error.response.data)
            contexto.setTypeAlert(TypeAlertEnum.error)
            contexto.setOpenAlert(true)
        })

    }

    const gerarDanfe = _ => {
        axios({
            url: `/venda/${entidade.id}/getdanfevenda`,
            method: 'GET',
            responseType: 'blob'
        }).then(resp => {
            try {
                const blob = new Blob([resp.data], { type: resp.headers['content-type'] });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;

                link.download = `gfl-danfe-${entidade.id}`;
                document.body.appendChild(link);
                link.click();

                //Limpa o objeto blob da memória
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
            } catch (error) {
                contexto.setMessageAlert("Erro ao baixar DANFE!")
                contexto.setTypeAlert(TypeAlertEnum.error)
                contexto.setOpenAlert(true)
            }
        }).catch(error => {
            if (error.response?.status === 400) {
                contexto.setMessageAlert("Erro ao gerar DANFE!")
                contexto.setTypeAlert(TypeAlertEnum.error)
                contexto.setOpenAlert(true)
            }
        });
    }

    const emitirNotaFiscal = async _ => {
        try {
            var novoStatus = await axios.post(`/venda/${entidade.id}/emitirnotafiscal`, null, { params: { avoidErrorFeedbackMessage: true } })
            setStatusNotaFiscal(novoStatus.data)

            contexto.setMessageAlert("Sucesso!")
            contexto.setTypeAlert(TypeAlertEnum.success)
            contexto.setOpenAlert(true)
        } catch (error) {
            if (error.response && error.response.data) {
                if (error.response.data.statusRetorno)
                    setStatusNotaFiscal(error.response.data)
                else
                    confirm({
                        title: 'Não foi possível emitir NFC-e',
                        description: error.response.data,
                        confirmationText: "OK",
                        hideCancelButton: true,
                        confirmationButtonProps: { autoFocus: true } 
                    }).then(() => {}).catch(() => { });   
            }

            contexto.setMessageAlert("Nota Rejeitada!")
            contexto.setTypeAlert(TypeAlertEnum.error)
            contexto.setOpenAlert(true)
        }
    }

    const formatarValorMonetario = (valor: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor)
    }

    const StatusNotaFiscal = _ => {
        if (!statusNotaFiscal)
            return null;
        
        if (isStatusSucessoNotaFiscal(statusNotaFiscal.statusRetorno))
            return <Typography component="span" sx={{ flex: 1, color: "green", fontWeight: "bold" }}>Autorizado</Typography>
        else if (isStatusCanceladoNotaFiscal(statusNotaFiscal.statusRetorno))
            return <Typography component="span" sx={{ flex: 1, color: "orange", fontWeight: "bold" }}>Cancelado</Typography>
        else if (!isStatusSucessoNotaFiscal(statusNotaFiscal.statusRetorno))
            return <Typography component="span" sx={{ flex: 1, color: "red", fontWeight: "bold" }}>Rejeitado</Typography>
    }

    const BotaoGerarDanfe = _ => {
        if (!statusNotaFiscal)
            return null;
        if (!isStatusSucessoNotaFiscal(statusNotaFiscal.statusRetorno))
            return null;

        return (
            <InfoVendaLinha sx={{ justifyContent: "flex-end" }}>
                <Button sx={{ float: "right" }} variant="outlined" autoFocus onClick={gerarDanfe}>
                    Gerar Danfe
                </Button>
            </InfoVendaLinha>
        )
    }

    const BotaoCancelarNotaFiscalConsumidor = _ => {
        if (!statusNotaFiscal)
            return null;
        if (!isStatusSucessoNotaFiscal(statusNotaFiscal.statusRetorno))
            return null;

        return (
            <InfoVendaLinha sx={{ justifyContent: "flex-end" }}>
                <Button sx={{ float: "right", color: "orange", borderColor: "orange" }} variant="outlined" autoFocus onClick={cancelarNFCe}>
                    Cancelar NFCe
                </Button>
            </InfoVendaLinha>
        )
    }

    const BotaoEmitirNotaFiscal = _ => {
        if (isStatusSucessoNotaFiscal(statusNotaFiscal?.statusRetorno))
            return null;

        var nomeBotao = possuiNotaFiscal ? "Reemitir Nota Fiscal" : "Emitir Nota Fiscal"

        return (
            <InfoVendaLinha sx={{ justifyContent: "flex-end" }}>
                <Button sx={{ float: "right" }} variant="outlined" autoFocus onClick={emitirNotaFiscal}>
                    {nomeBotao}
                </Button>
            </InfoVendaLinha>
        )
    }

    return (
        <Dialog
            fullScreen={fullScreen}
            open={entidade !== null}
            onClose={handleFechar}
        >
            <DialogContent>
                <InfoVenda>
                    <Typography variant="h6" fontWeight={"bold"} component="h2">Informações da Venda</Typography>
                    <InfoVendaLinha>
                        <InfoVendaLabel>Data da Venda:</InfoVendaLabel>
                        <InfoVendaValor>{new Date(entidade.data).toLocaleString().slice(0, -3)}</InfoVendaValor>
                    </InfoVendaLinha>
                    <InfoVendaLinha>
                        <InfoVendaLabel>Forma Pagamento:</InfoVendaLabel>
                        <InfoVendaValor>{obterDescricaoFormaPagamento(entidade.formaPagamento)}</InfoVendaValor>
                    </InfoVendaLinha>
                    <InfoVendaLinha>
                        <InfoVendaLabel>Valor Desconto:</InfoVendaLabel>
                        <InfoVendaValor>{formatarValorMonetario(entidade.valorDesconto)}</InfoVendaValor>
                    </InfoVendaLinha>
                    <InfoVendaLinha>
                        <InfoVendaLabel>Total:</InfoVendaLabel>
                        <InfoVendaValor>{formatarValorMonetario(entidade.valorTotal)}</InfoVendaValor>
                    </InfoVendaLinha>
                </InfoVenda>
                <Divider sx={{ my: 2, display: possuiNotaFiscal ? "flex" : "none" }} />
                <Content>
                    {!possuiNotaFiscal ? null : (
                        <>
                            <Typography variant="h6" fontWeight={"bold"} component="h2">Nota Fiscal</Typography>
                            <InfoVendaLinha>
                                <InfoVendaLabel>Status Retorno:</InfoVendaLabel>
                                <StatusNotaFiscal />    
                            </InfoVendaLinha>
                            <InfoVendaLinha>
                                <InfoVendaLabel>Mensagem Retorno:</InfoVendaLabel>
                                <InfoVendaValor>{statusNotaFiscal.mensagemRetorno}</InfoVendaValor>
                            </InfoVendaLinha>
                            <Divider sx={{ my: 2 }} />
                            <BotaoGerarDanfe />
                            <BotaoCancelarNotaFiscalConsumidor />
                        </>
                    )}
                    <BotaoEmitirNotaFiscal />
                    <Button onClick={handleFechar} variant="outlined">
                        Fechar
                    </Button>
                </Content>
            </DialogContent>
        </Dialog>
    )
}

export default VendaDialog
